import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { ArrowRight, MessageSquare, CalendarCheck, BarChart3, Command, Users, Plug, Eye, Brain, Shield, Zap, Target, TrendingUp } from 'lucide-react';
import { DemoContactForm } from '@/components/sections/DemoContactForm';

const pillars = [
  {
    icon: MessageSquare,
    title: 'AI Assistant',
    tagline: 'Your Revenue Co-Pilot',
    description: 'Talk to your CRM like you would talk to a colleague. Paste a screenshot, snap a business card, or just describe what happened. The AI handles the rest.',
    features: [
      'Natural language CRM updates',
      'Zero-touch data entry from conversations',
      'Image intelligence: business cards, LinkedIn, email signatures',
      'Smart enrichment with verified company data',
    ],
    color: 'from-primary/20 to-primary/5',
  },
  {
    icon: CalendarCheck,
    title: 'Meeting Intelligence',
    tagline: 'Never Miss a Signal',
    description: 'Connects to Google Meet, Microsoft Teams, and Zoom. Records, transcribes, and summarizes every sales conversation without anyone pressing a button.',
    features: [
      'Automatic capture across all video platforms',
      'AI meeting prep with deal context and stakeholder maps',
      'Post-meeting CRM updates and follow-up tasks',
      'Coaching insights: talk-time, objections, competitive mentions',
    ],
    color: 'from-blue-500/20 to-blue-500/5',
  },
  {
    icon: BarChart3,
    title: 'Pipeline Intelligence',
    tagline: 'Evidence Over Optimism',
    description: 'Every deal is scored on verified evidence, not rep optimism. Missing MEDDIC fields, stale engagement, and hygiene gaps are penalized automatically.',
    features: [
      'Evidence-based health scores',
      'Buyer engagement tracking (email velocity, meetings, multi-threading)',
      'Going-dark alerts via Slack',
      'Pipeline velocity analytics per stage',
    ],
    color: 'from-amber-500/20 to-amber-500/5',
  },
  {
    icon: Command,
    title: 'Revenue Command Center',
    tagline: "The CRO's War Room",
    description: 'A single view: pipeline movement, forecast deltas, rep performance, and coaching opportunities. Updated daily.',
    features: [
      'Executive brief with daily pipeline snapshots',
      'Evidence-based forecasting with backtest calibration',
      'Monday Morning Digest delivered to your inbox',
      'Board-ready narratives for QBRs and investor updates',
    ],
    color: 'from-purple-500/20 to-purple-500/5',
  },
  {
    icon: Users,
    title: 'Team Management',
    tagline: 'Built for Scale',
    description: 'Three-tier RBAC, drag-and-drop team building, and rep performance dashboards. Every feature respects role boundaries.',
    features: [
      'CRO, Manager, and Rep role tiers',
      'Activity tracking and conversion metrics per rep',
      'Team structure management with invitations',
      'Pipeline contribution dashboards',
    ],
    color: 'from-green-500/20 to-green-500/5',
  },
  {
    icon: Plug,
    title: 'Integrations Hub',
    tagline: 'Connect Everything',
    description: 'Full read/write with HubSpot, Salesforce, and Pipedrive. Plus Google Workspace, Microsoft 365, Zoom, and Slack.',
    features: [
      'CRM: HubSpot, Salesforce, Pipedrive with field mapping',
      'Calendar & Email: Google Workspace, Microsoft 365',
      'Video: Zoom, Google Meet, Microsoft Teams',
      'Messaging: Slack for risk alerts and CRO digests',
    ],
    color: 'from-teal-500/20 to-teal-500/5',
  },
];

const differentiators = [
  { icon: Eye, title: 'Zero-Touch CRM', desc: 'Other tools sit on top of your CRM. NoQ5 is the interface. Talk, paste, snap a photo. Your CRM stays perfect because humans never touch it.' },
  { icon: Brain, title: 'Evidence Over Optimism', desc: 'NoQ5 scores every deal on verified evidence: email engagement, meeting cadence, stakeholder coverage, CRM completeness.' },
  { icon: Target, title: 'One Platform, Three CRMs', desc: 'HubSpot, Salesforce, or Pipedrive. NoQ5 works identically. Switch CRMs without switching your intelligence.' },
  { icon: TrendingUp, title: 'AI That Learns Your Business', desc: 'Adapts to your methodology (MEDDIC, BANT, SPICED), your ICP, your competitive landscape. Gets smarter every week.' },
  { icon: Shield, title: 'Built for European Sales Teams', desc: 'GDPR-compliant by design. Data retention controls. Multi-language. Timezone-aware. Designed for European B2B.' },
];

export default function ProductPlatform() {
  return (
    <Layout>
      <SEOHead
        title="NoQ5 Core Platform | AI Chief of Staff for Revenue Teams"
        description="AI Assistant, Meeting Intelligence, Pipeline Intelligence, and Revenue Command Center. Zero-touch CRM for sales-driven teams. GDPR compliant."
        canonicalUrl="https://noq5.ai/product/platform"
        keywords="NoQ5 platform, AI CRM automation, meeting intelligence, pipeline intelligence, revenue command center, MEDDIC scoring, zero-touch CRM, AI revenue agent"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What is the NoQ5 Core Platform?", "acceptedAnswer": { "@type": "Answer", "text": "NoQ5 Core is an AI Chief of Staff for revenue teams. It includes an AI Assistant for natural language CRM updates, Meeting Intelligence for automatic call capture and summarization, Pipeline Intelligence with MEDDIC/BANT/SPICED scoring, and a Revenue Command Center for CROs." }},
            { "@type": "Question", "name": "How does zero-touch CRM work?", "acceptedAnswer": { "@type": "Answer", "text": "NoQ5 automatically captures data from meetings, emails, and conversations, then updates your CRM (HubSpot, Salesforce, or Pipedrive) without any manual data entry. You can also paste screenshots, business cards, or describe updates in natural language." }},
            { "@type": "Question", "name": "What CRMs does NoQ5 support?", "acceptedAnswer": { "@type": "Answer", "text": "NoQ5 works with HubSpot, Salesforce, and Pipedrive with identical feature parity across all three. It also integrates with Google Workspace, Slack, Zoom, and Microsoft Teams." }},
            { "@type": "Question", "name": "Is NoQ5 GDPR compliant?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. NoQ5 is EU-hosted, uses AES-256 encryption, row-level tenant isolation, configurable data retention, and automatic PII controls. Built for European B2B sales teams." }},
          ]
        }]}
      />
      <Breadcrumbs items={[{ label: "Product", href: "/product/platform" }, { label: "Core Platform", href: "/product/platform" }]} />

      {/* Hero */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/8 border border-primary/15 mb-8">
                <Zap className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground/80">Core Platform at EUR 59/user/month</span>
              </div>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.08] mb-6"
            >
              <span className="text-foreground">The Zero-Touch CRM </span>
              <span className="text-gradient-teal">Intelligence Platform</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              While other tools help salespeople use their CRM, NoQ5 replaces the need to. The AI works 24/7 as an autonomous revenue operator.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-3 justify-center"
            >
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-sm shadow-button hover:shadow-none transition-all group" asChild>
                <a href="#book-demo">
                  Contact Us <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="font-bold px-8 py-6 text-lg rounded-sm border-2 border-primary/25 hover:bg-primary/5" asChild>
                <a href="#book-demo">Talk to Sales</a>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Pillars */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">Everything Your Revenue Team Needs</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Six integrated modules working together so your team sells instead of doing admin.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {pillars.map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-sm p-6 hover:shadow-lg transition-shadow"
              >
                <div className={`inline-flex p-3 rounded-sm bg-gradient-to-br ${pillar.color} mb-4`}>
                  <pillar.icon className="w-6 h-6 text-foreground" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-1">{pillar.title}</h3>
                <p className="text-sm font-medium text-primary mb-3">{pillar.tagline}</p>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{pillar.description}</p>
                <ul className="space-y-2">
                  {pillar.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">Why Teams Choose NoQ5</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {differentiators.map((d, i) => (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="p-6 rounded-sm border border-border bg-card"
              >
                <d.icon className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-lg font-bold text-foreground mb-2">{d.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Diagram */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-foreground">Platform Architecture</h2>
          <div className="bg-card border border-border rounded-sm p-6 md:p-8 text-left">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-primary/10 rounded-sm p-4 text-center">
                <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">AI Engine</p>
                <p className="text-xs text-muted-foreground">Chat, Vision, Enrich</p>
              </div>
              <div className="bg-primary/10 rounded-sm p-4 text-center">
                <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">CRM Bridge</p>
                <p className="text-xs text-muted-foreground">HubSpot, Salesforce, Pipedrive</p>
              </div>
              <div className="bg-primary/10 rounded-sm p-4 text-center">
                <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">Meeting Intel</p>
                <p className="text-xs text-muted-foreground">Auto-capture, Transcription</p>
              </div>
            </div>
            <div className="bg-primary/5 rounded-sm p-4 text-center mb-4">
              <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">Revenue Intelligence</p>
              <p className="text-xs text-muted-foreground">Health Scoring, Forecast, Coaching, Engagement, Alerts, Digest</p>
            </div>
            <div className="bg-muted rounded-sm p-4 text-center">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Integration Layer</p>
              <p className="text-xs text-muted-foreground">Google, Microsoft, Zoom, Slack, LinkedIn</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background">
        <div className="container px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { stat: '81', label: 'Backend AI Functions' },
              { stat: '298', label: 'UI Components' },
              { stat: '3', label: 'CRM Integrations' },
              { stat: '24/7', label: 'Autonomous Operation' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-3xl md:text-4xl font-bold text-primary">{s.stat}</p>
                <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DemoContactForm />
    </Layout>
  );
}
